/**
 * AppBreadcrumbs — auto-derived breadcrumb trail for member pages.
 *
 * Uses the current pathname + a small route→label map to render a
 * Home / … / current-page trail styled against the HomeLyph Pro tokens.
 *
 * Falls back to a Title-Cased slug if we don't recognise the path.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, ChevronRight } from 'lucide-react';

// Human labels for every top-level member/admin route.
const ROUTE_LABELS = {
  dashboard: 'Dashboard',
  portal: 'My Portal',
  learning: 'Learning',
  course: 'Course',
  lesson: 'Lesson',
  videos: 'Video Library',
  'ai-chat': 'AI Assistant',
  calculators: 'Calculators',
  business: 'Business Calculator',
  'household-calculator': 'Household Calculator',
  recipes: 'Recipe Builder',
  database: 'Freeze Dry Database',
  'batch-tracker': 'Batch Tracker',
  'batch-history': 'Batch History',
  forum: 'Community',
  store: 'Store',
  directory: 'Directory',
  partners: 'Partners',
  consulting: 'Consulting',
  pricing: 'Pricing',
  admin: 'Admin',
  analytics: 'Analytics',
  users: 'User Management',
  lms: 'LMS Admin',
  onboarding: 'Onboarding',
  survey: 'Survey',
  new: 'Getting Started',
};

const prettify = (slug) => {
  if (!slug) return '';
  if (ROUTE_LABELS[slug]) return ROUTE_LABELS[slug];
  // dynamic ids (mongo objectid-ish or numeric)
  if (/^[a-f0-9]{20,}$/i.test(slug) || /^\d+$/.test(slug)) return null;
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const AppBreadcrumbs = ({ items }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // If items are passed explicitly, honour them; otherwise auto-derive.
  let crumbs = items;
  if (!crumbs) {
    const parts = location.pathname.split('/').filter(Boolean);
    let acc = '';
    crumbs = parts
      .map((p) => {
        acc += `/${p}`;
        const label = prettify(p);
        return label ? { label, href: acc } : null;
      })
      .filter(Boolean);
  }

  if (!crumbs.length) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="border-b border-border/60 bg-background/80"
      data-testid="app-breadcrumbs"
    >
      <div className="container mx-auto px-6 py-2.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground overflow-x-auto no-scrollbar">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
          data-testid="breadcrumb-home"
        >
          <Home className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Home</span>
        </button>
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <React.Fragment key={c.href}>
              <ChevronRight className="w-3.5 h-3.5 opacity-60 flex-shrink-0" />
              {last ? (
                <span
                  aria-current="page"
                  className="text-foreground font-semibold truncate max-w-[220px]"
                  data-testid={`breadcrumb-current`}
                >
                  {c.label}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate(c.href)}
                  className="hover:text-foreground transition-colors truncate max-w-[160px]"
                  data-testid={`breadcrumb-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {c.label}
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
};

export default AppBreadcrumbs;
